// Image slides rotate on Slick's fixed timer. Video slides are longer than that
// timer (14-17s vs 6s), so while a video slide is active we hold the carousel
// and only move on once the video has played through.
const IMAGE_SLIDE_MS = 6000;
// If a video slide never starts (autoplay blocked, save-data, failed load) fall
// back to the image timing so the carousel can't stall on it.
const VIDEO_FALLBACK_MS = IMAGE_SLIDE_MS;
// Hard cap once a video *is* playing, in case it stalls mid-stream.
const VIDEO_STALL_GRACE_MS = 5000;

let releaseActiveVideo = null;

const safePlay = video => {
  const playPromise = video.play();
  if (playPromise && typeof playPromise.catch === 'function') {
    playPromise.catch(() => {});
  }
};

// Mobile browsers refuse to begin autoplay for a video that isn't actually
// visible. In Slick's fade mode inactive slides sit in the DOM at opacity: 0,
// so a background video on any slide other than the first never starts playing
// on mobile. Slick's own slide-change events aren't wired to the per-section
// retry script, so nothing recovers it when the slide fades into view. Play the
// active slide's video from the start (and pause the rest) whenever the active
// slide changes, holding Slick's autoplay until that video ends.
const playActiveSlideVideo = ($slider, slick, index) => {
  if (releaseActiveVideo) {
    releaseActiveVideo();
    releaseActiveVideo = null;
  }

  let activeVideo = null;

  slick.$slides.each((slideIndex, slide) => {
    const video = slide.querySelector('[data-hero-26-video]');
    if (!video) return;

    if (slideIndex === index) {
      activeVideo = video;
    } else if (!video.paused) {
      video.pause();
    }
  });

  if (!activeVideo) {
    // Image slide: normal timed rotation.
    $slider.slick('slickPlay');
    return;
  }

  // Video slide: hold the carousel, play the video through once, then advance.
  // The <video> is marked `loop` for the single-banner case; in the carousel we
  // need `ended` to fire, so switch looping off here.
  $slider.slick('slickPause');
  activeVideo.loop = false;
  try {
    activeVideo.currentTime = 0;
  } catch (e) {
    // Metadata not loaded yet (preload="none") — play() starts from 0 anyway.
  }

  let fallbackTimer = null;
  let stallTimer = null;

  const cleanup = () => {
    clearTimeout(fallbackTimer);
    clearTimeout(stallTimer);
    activeVideo.removeEventListener('ended', advance);
    activeVideo.removeEventListener('error', advance);
    activeVideo.removeEventListener('playing', onPlaying);
    activeVideo.removeEventListener('timeupdate', onTimeUpdate);
  };

  const advance = () => {
    cleanup();
    releaseActiveVideo = null;
    $slider.slick('slickNext');
  };

  const armStallTimer = () => {
    clearTimeout(stallTimer);
    const remaining = Number.isFinite(activeVideo.duration)
      ? Math.max(0, activeVideo.duration - activeVideo.currentTime) * 1000
      : VIDEO_FALLBACK_MS;
    stallTimer = setTimeout(advance, remaining + VIDEO_STALL_GRACE_MS);
  };

  const onPlaying = () => {
    clearTimeout(fallbackTimer);
    armStallTimer();
  };

  // Re-arm the stall cap as playback progresses so buffering pauses don't
  // trip it while the video is genuinely still playing.
  const onTimeUpdate = () => {
    if (stallTimer) armStallTimer();
  };

  activeVideo.addEventListener('ended', advance);
  activeVideo.addEventListener('error', advance);
  activeVideo.addEventListener('playing', onPlaying);
  activeVideo.addEventListener('timeupdate', onTimeUpdate);
  fallbackTimer = setTimeout(advance, VIDEO_FALLBACK_MS);

  releaseActiveVideo = cleanup;

  safePlay(activeVideo);
};

// The schema `class: "homepage-slider"` sits on the section *wrapper* (#shopify-section-…),
// while the banner settings (data-hero-order / data-hero-start) are on the inner
// `.hero-26`. Read the dataset from the inner element, falling back to the wrapper.
const heroData = element => (element.querySelector('.hero-26') || element).dataset;

// Read a banner's "Position in slider" setting. Missing/blank orders sort to the
// end (Infinity) so an unordered banner never steals the first slot.
const getHeroOrder = element => {
  const order = parseInt(heroData(element).heroOrder, 10);
  return Number.isNaN(order) ? Infinity : order;
};

const heroSlider = () => {
  const sliderElements = document.querySelectorAll('.homepage-slider');

  if (sliderElements.length > 1) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('homepage-slider-wrapper');

    //Get the first element's parent.
    const parent = sliderElements[0].parentNode;

    // Insert the new wrapper *before* the first element.
    parent.insertBefore(wrapper, sliderElements[0]);

    // Order the slides by their "Position in slider" setting before appending, so
    // the banner set to 1 shows first regardless of section order in the template.
    const orderedElements = Array.from(sliderElements).sort(
      (a, b) => getHeroOrder(a) - getHeroOrder(b)
    );

    // Add the elements into the wrapper
    orderedElements.forEach(element => {
      wrapper.appendChild(element);
    });

    // Open the carousel on the banner ticked as "Start slider on this banner".
    // Position order (and the H1 on position 1) is unchanged — this only sets
    // which slide is active on load. Defaults to the first slide if none ticked.
    const startIndex = orderedElements.findIndex(
      element => heroData(element).heroStart === 'true'
    );
    const initialSlide = startIndex < 0 ? 0 : startIndex;

    const $slider = $('.homepage-slider-wrapper');

    // Play the video on whichever slide becomes active.
    $slider.on('afterChange', (event, slick, currentSlide) => {
      playActiveSlideVideo($slider, slick, currentSlide);
    });

    // Init slick
    $slider.slick({
      autoplay: true,
      autoplaySpeed: IMAGE_SLIDE_MS,
      dots: true,
      arrows: false,
      infinite: true,
      fade: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      adaptiveHeight: false,
      pauseOnHover: false,
      pauseOnFocus: false,
      initialSlide: initialSlide,
    });

    // Kick off the initial active slide's video (afterChange only fires on
    // subsequent transitions).
    const slickInstance = $slider.slick('getSlick');
    playActiveSlideVideo($slider, slickInstance, slickInstance.currentSlide);
  }
};

const heroReviewsScroll = () => {
  const reviewsElements = document.querySelectorAll('.hero-26__reviews');
  if (reviewsElements.length > 0) {
    const handleClick = () => {
      const targetElement = document.getElementById('reviewsio-carousel-widget');
      if (targetElement) {
        const targetPosition = targetElement.getBoundingClientRect().top + window.scrollY - 50;
        window.scrollTo({ top: targetPosition, behavior: 'smooth' });
      }
    };

    reviewsElements.forEach(reviewsElement => {
      reviewsElement.addEventListener('click', handleClick);
      reviewsElement.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      });
    });
  }
};

export { heroSlider, heroReviewsScroll };
